import BaseController from "./base_controller"

export default class extends BaseController {
  static targets = [
    "step1", "step2", "step3",
    "step1Badge", "step2Badge", "step3Badge",
    "nextButton1", "nextButton2",
    "form"
  ]
  static values = {
    validationError: String
  }
  
  declare readonly step1Target: HTMLElement
  declare readonly step2Target: HTMLElement
  declare readonly step3Target: HTMLElement
  declare readonly step1BadgeTarget: HTMLElement
  declare readonly step2BadgeTarget: HTMLElement
  declare readonly step3BadgeTarget: HTMLElement
  declare readonly nextButton1Target: HTMLElement
  declare readonly nextButton2Target: HTMLElement
  declare readonly formTarget: HTMLFormElement
  declare readonly validationErrorValue: string
  declare readonly hasValidationErrorValue: boolean
  
  private currentStep: number = 1
  
  connect(): void {
    super.connect()
    this.currentStep = 1
    this.debug("Onboarding wizard connected")
  }
  
  nextStep(): void {
    if (!this.validateCurrentStep()) {
      this.showErrorMessage(this.validationErrorValue)
      return
    }
    
    this.hideStep(this.currentStep)
    this.currentStep++
    this.showStep(this.currentStep)
    this.updateBadges()
  }
  
  previousStep(): void {
    this.hideStep(this.currentStep)
    this.currentStep--
    this.showStep(this.currentStep)
    this.updateBadges()
  }
  
  showStep(step: number): void {
    const target = this[`step${step}Target` as keyof this] as HTMLElement
    if (target && target.classList) {
      target.classList.remove('hidden')
      const card = this.element.querySelector<HTMLElement>('.onboarding-card')
      if (card) card.scrollTop = 0
      const heading = target.querySelector<HTMLElement>('h2')
      heading?.focus({ preventScroll: true })
      heading?.scrollIntoView({ block: 'nearest', behavior: 'instant' })
    }
  }
  
  hideStep(step: number): void {
    const target = this[`step${step}Target` as keyof this] as HTMLElement
    if (target && target.classList) {
      target.classList.add('hidden')
    }
  }
  
  updateBadges(): void {
    [1, 2, 3].forEach(step => {
      const stepElement = this[`step${step}BadgeTarget` as keyof this] as HTMLElement
      if (!stepElement || !stepElement.classList) return
      
      if (step <= this.currentStep) {
        // Completed step or current step - use step-neutral (black)
        stepElement.classList.add('step-neutral')
        stepElement.classList.remove('step-primary')
      } else {
        // Future step - no special class (white/default styling)
        stepElement.classList.remove('step-primary', 'step-neutral')
      }
    })
  }
  
  validateCurrentStep(): boolean {
    switch(this.currentStep) {
      case 1:
        return this.validateStep1()
      case 2:
        return this.validateStep2()
      case 3:
        return true // Step 3 has defaults, always valid
      default:
        return false
    }
  }
  
  validateStep1(): boolean {
    const substanceSelect = this.formTarget.querySelector<HTMLSelectElement>('[name="user[current_substance_id]"]')
    const doseAmount = this.formTarget.querySelector<HTMLInputElement>('[name="user[current_dose_amount]"]')
    const doseUnit = this.formTarget.querySelector<HTMLInputElement>('[name="user[current_dose_unit]"]')
    
    return !!(substanceSelect?.value && doseAmount?.value && doseUnit?.value && doseAmount.checkValidity())
  }
  
  validateStep2(): boolean {
    const protocolSelect = this.formTarget.querySelector<HTMLSelectElement>('[name="user[current_protocol_id]"]')
    return !!protocolSelect?.value
  }
  
  showErrorMessage(message: string): void {
    // Simple error display - could be enhanced with toast notifications
    alert(message)
  }
}
